"use client";

import { clsx } from "clsx";
import { useContactSalesForm } from "./hooks/useContactSalesForm";
import { CONTACT_SALES_VARIANT_PROGRESS_STEPS } from "./shared/constants";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";
import { ResumePrompt } from "./components/ResumePrompt";
import { StepNavigation } from "./components/StepNavigation";
import { TestVariantFormFooter } from "./components/TestVariantFormFooter";
import { CompactFormCard } from "./CompactFormCard";
import { isTestVariantId, type CsfVersionId } from "./shared/constants";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import NextImage from "next/image";
import { useTranslations } from "next-intl";

const FADE_VARIANTS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
} as const;

type ContactSalesFormProps = {
  csfVersionId: CsfVersionId;
  g2BannerAlt?: string;
};

export function ContactSalesForm({ csfVersionId, g2BannerAlt }: ContactSalesFormProps) {
  const isTestVariant = isTestVariantId(csfVersionId);
  const tPage = useTranslations("ClientKeys.contact_sales_page");
  const { form, step, stepIndex, showResumePrompt, continueWithSavedForm, startFreshForm, steps } = useContactSalesForm(
    { isTestVariant, csfVersionId },
  );

  const currentStepData = steps[stepIndex];

  return (
    <CompactFormCard>
      <form
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div className='tw-mb-4 sm:tw-mb-6 lg:tw-mb-8'>
          <AnimatePresence mode='wait'>
            <div key={`compact-header-${step}`}>
              {isTestVariant && step === 1 && (
                <m.div
                  className='lg:tw-hidden tw-mb-4 tw-flex tw-justify-center'
                  initial={false}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <NextImage
                    src='/assets/contact-sales/g2-awards-banner.png'
                    alt={tPage("g2_awards_banner_alt")}
                    width={800}
                    height={120}
                    className='tw-w-full tw-h-auto'
                    priority
                  />
                </m.div>
              )}
              <m.h2
                className={clsx(
                  "tw-mb-3 sm:tw-mb-4 lg:tw-mb-6",
                  isTestVariant
                    ? "tw-type-2xl sm:tw-type-4xl lg:tw-type-5xl"
                    : "tw-type-3xl sm:tw-type-4xl lg:tw-type-5xl",
                )}
                initial={false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {currentStepData.title}
              </m.h2>
              <m.p
                className={clsx(
                  "tw-mb-8 sm:tw-mb-12 tw-opacity-60 lg:tw-mb-9",
                  isTestVariant ? "tw-type-xs sm:tw-type-sm md:tw-type-base" : "tw-type-sm sm:tw-type-base",
                )}
                initial={false}
                animate={{ opacity: 0.6 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {currentStepData.subtitle}
              </m.p>
            </div>
          </AnimatePresence>
        </div>
        <form.Subscribe selector={state => state.isSubmitting}>
          {isSubmitting => (
            <div className='tw-relative'>
              <AnimatePresence mode='wait'>
                <m.div
                  key={`compact-step-${step}`}
                  className='max-lg:tw-animate-in max-lg:tw-fade-in max-lg:tw-duration-200'
                  initial={false}
                  animate='animate'
                  exit='exit'
                  variants={FADE_VARIANTS}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    className={clsx(
                      "tw-transition-opacity tw-duration-300",
                      isSubmitting && "tw-opacity-30 tw-pointer-events-none",
                    )}
                  >
                    {currentStepData.component}
                  </div>
                </m.div>
              </AnimatePresence>
              {isSubmitting && (
                <m.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className='tw-absolute tw-top-1/3 tw-left-1/2 tw--translate-x-1/2 tw--translate-y-1/2'
                >
                  <Spinner20 className='tw-text-gray-900 tw-scale-150' />
                </m.div>
              )}
            </div>
          )}
        </form.Subscribe>
        <AnimatePresence mode='wait'>
          <m.div
            key={`compact-nav-${step}`}
            initial={false}
            animate='animate'
            exit='exit'
            variants={FADE_VARIANTS}
            transition={{ duration: 0.2 }}
          >
            {isTestVariant ? (
              currentStepData.navigation && (
                <TestVariantFormFooter
                  navigation={currentStepData.navigation}
                  currentStep={step}
                  totalSteps={CONTACT_SALES_VARIANT_PROGRESS_STEPS}
                  form={form}
                />
              )
            ) : (
              <StepNavigation
                navigation={currentStepData.navigation}
                currentStep={step}
                totalSteps={steps.length}
                form={form}
              />
            )}
          </m.div>
        </AnimatePresence>
        <AnimatePresence>
          {showResumePrompt && <ResumePrompt onContinue={continueWithSavedForm} onStartFresh={startFreshForm} />}
        </AnimatePresence>
      </form>
    </CompactFormCard>
  );
}
